import type { FirestoreSettings } from 'firebase/firestore';

/** iPadOS 13+ reports itself as "MacIntel", so the touch-point check is required. */
const isIOSDevice = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Safari 26.4+ withholds streamed fetch() bytes until the next network chunk (WebKit bug 322545,
// firebase-js-sdk#9789), which stalls listeners for up to 30 s. Every iOS/iPadOS browser runs WebKit.
const isWebKitSession = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const isMacSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
  return isIOSDevice() || isMacSafari;
};

// Debug switch to compare transports on one device: localStorage 'physiofit:firestore-transport' =
// 'sdk-default' (also on WebKit) or 'long-polling' (any browser). Read once at boot.
const readTransportOverride = (): 'sdk-default' | 'long-polling' | null => {
  try {
    const value = window.localStorage.getItem('physiofit:firestore-transport');
    return value === 'sdk-default' || value === 'long-polling' ? value : null;
  } catch {
    return null;
  }
};

/** `useFetchStreams` is a private SDK setting (true in browsers); false moves WebChannel from fetch() to XHR. */
export const firestoreTransportSettings = (): FirestoreSettings & { useFetchStreams?: boolean } => {
  const transport = readTransportOverride() ?? (isWebKitSession() ? 'long-polling' : 'sdk-default');
  return transport === 'long-polling' ? { experimentalForceLongPolling: true, useFetchStreams: false } : {};
};
